import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { setLocationsCache } from '../../redux/bookingHub';
import { getBookingHubLocationsById, getBookingHubTimeSlots } from '../../api';
import '../../assets/css/bookingHub.css';

const BookingHubLocation = ({ match }) => {
  const { gid, locationGid } = match.params;
  const history = useHistory();
  const dispatch = useDispatch();
  const { locationsByGid } = useSelector(state => state.bookingHub);
  const [location, setLocation] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (locationsByGid[gid]) {
        const found = locationsByGid[gid].find(l => l.gid === locationGid || l.bookingHubGid === locationGid);
        if (found) {
          setLocation(found);
          setLoading(false);
        }
      }

      try {
        const timeSlotsEntity = await getBookingHubTimeSlots(locationGid);
        const found = timeSlotsEntity?.location;
        if (found) {
          setLocation(found);
          // Update cache with the new location info
          const currentList = locationsByGid[gid] || [];
          const updatedLocations = currentList.map(l => l.gid === locationGid ? found : l);
          if (!updatedLocations.some(l => l.gid === locationGid)) {
            updatedLocations.push(found);
          }
          dispatch(setLocationsCache({ gid, locations: updatedLocations }));
        }
        if (timeSlotsEntity?.timeSlots) {
          setTimeSlots(timeSlotsEntity.timeSlots);
        }
      } catch (error) {
        console.error("Error fetching location data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [gid, locationGid, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatAddress = (addr) => {
    if (!addr) return null;
    if (typeof addr === 'string') return addr;
    const parts = [addr.street, addr.city, addr.state, addr.postalCode].filter(Boolean);
    return parts.join(', ');
  };

  const getTodayOperatingHours = () => {
    const todayStr = moment().format('YYYY-MM-DD');
    const todayEntry = timeSlots.find(ts => ts.date === todayStr);
    
    if (todayEntry && todayEntry.timeSlots && todayEntry.timeSlots.length > 0) {
      const formatTimeLabel = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMins = mins.toString().padStart(2, '0');
        return `${displayHours}:${displayMins} ${ampm}`;
      };

      const slotStrings = todayEntry.timeSlots.map(slot => {
        return `${formatTimeLabel(slot.startTime)} - ${formatTimeLabel(slot.endTime)}`;
      });
      return `Today: ${slotStrings.join(', ')}`;
    }

    if (location?.operatingHours && location.operatingHours.length > 0) {
      const todayDayName = moment().format('dddd').toUpperCase();
      const todayHours = location.operatingHours.find(h => h.dayOfWeek === todayDayName);
      const slots = todayHours?.timeSlots || todayHours?.timeSlot;
      
      const openSlots = slots?.filter(s => s.isOpen && s.openingTime !== undefined && s.openingTime !== null && s.openingTime !== '' && s.closingTime !== undefined && s.closingTime !== null && s.closingTime !== '') || [];
      
      if (openSlots.length > 0) {
        const formatTimeLabel = (minutes) => {
          const hours = Math.floor(minutes / 60);
          const mins = minutes % 60;
          const ampm = hours >= 12 ? 'PM' : 'AM';
          const displayHours = hours % 12 || 12;
          const displayMins = mins.toString().padStart(2, '0');
          return `${displayHours}:${displayMins} ${ampm}`;
        };

        const slotStrings = openSlots.map(slot => {
          return `${formatTimeLabel(slot.openingTime)} - ${formatTimeLabel(slot.closingTime)}`;
        });
        return `Today: ${slotStrings.join(', ')}`;
      }
      
      return 'Today closed. Check other dates';
    }

    return location?.openingHours || '6:00 AM – 6:00 PM';
  };

  if (loading) {
    return (
      <div className="bh-detail-loading">
        <div className="bh-detail-spinner" />
      </div>
    );
  }
  if (!location) return <div className="bh-detail-not-found">Location not found</div>;

  const primaryColor = location.colorCode || '#B85C00';

  return (
    <div className="bh-detail-page">
      {/* Navigation Bar */}
      {history.location.state?.fromListing && (
        <nav className="bh-detail-nav">
          <button
            className="bh-detail-back-btn"
            onClick={() => history.goBack()}
            style={{ color: primaryColor }}
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.375 3.125L5.625 7.5L9.375 11.875" stroke={primaryColor} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to All Sites
          </button>

          <div className="bh-detail-nav-tags">
            {location.badge && (
              <span className="bh-detail-tag bh-detail-tag-primary" style={{ color: primaryColor, borderColor: `${primaryColor}80`, background: `${primaryColor}10` }}>
                {location.badge}
              </span>
            )}
            {location.dynasty && (
              <span className="bh-detail-tag bh-detail-tag-secondary" style={{ color: primaryColor, borderColor: `${primaryColor}25`, background: `${primaryColor}0b` }}>
                {location.dynasty}
              </span>
            )}
          </div>

          {location.logoUrl && (
            <img src={location.logoUrl} alt="Logo" className="bh-detail-nav-logo" />
          )}
        </nav>
      )}


      {/* Hero Banner */}
      <div className="bh-detail-hero">
        {location.bannerImages?.[0] && (
          <img src={location.bannerImages[0]} alt={location.name} className="bh-detail-hero-img" />
        )}
        <div className="bh-detail-hero-gradient" />
        <div className="bh-detail-hero-content">
          <div className="bh-detail-hero-bottom">
            <div>
              <div className="bh-detail-hero-title">{location.landmark}</div>
              {location.localName && (
                <p className="bh-detail-hero-subtitle">{location.localName}</p>
              )}
                {location.name && (
            <p className="bh-detail-hero-pre">{location.name.toUpperCase()}</p>
          )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bh-detail-body" style={{ background: '#f7f3ec' }}>
        {/* Left Column */}
        <div className="bh-detail-left">

          {/* Info Cards Row */}
          <div className="bh-detail-info-row">
            {getTodayOperatingHours() && (
              <div className="bh-detail-info-card">
                <div className="bh-detail-info-icon" style={{ background: `${primaryColor}14` }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="6.667" stroke={primaryColor} strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 5.333V8l2.667 2.667" stroke={primaryColor} strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <p className="bh-detail-info-label" style={{ color: `${primaryColor}99` }}>OPENING HOURS</p>
                  <p className="bh-detail-info-value" style={{ color: '#1c1409' }}>{getTodayOperatingHours()}</p>
                </div>
              </div>
            )}
          {location.address && (
              <div className="bh-detail-info-card">
                <div className="bh-detail-info-icon" style={{ background: `${primaryColor}14` }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 14.667S2.667 10 2.667 6a5.333 5.333 0 0 1 10.666 0c0 4-5.333 8.667-5.333 8.667z" stroke={primaryColor} strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="8" cy="6" r="1.333" stroke={primaryColor} strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <p className="bh-detail-info-label" style={{ color: `${primaryColor}99` }}>ADDRESS</p>
                  <p className="bh-detail-info-value" style={{ color: '#1c1409' }}>{formatAddress(location.address)}</p>
                </div>
              </div>
            )}
          </div>

          {/* About Section */}
          {location.description && (
            <div className="bh-detail-section">
              <div className="bh-detail-section-title" style={{ color: `${primaryColor}99` }}>ABOUT THIS SITE</div>
              <p className="bh-detail-section-text" style={{ color: primaryColor, lineHeight: '1.6' }}>
                {isDescExpanded || location.description.length <= 250
                  ? location.description
                  : `${location.description.slice(0, 250)}...`}
                {location.description.length > 250 && (
                  <span
                    style={{ color: primaryColor, fontWeight: 'bold', cursor: 'pointer', marginLeft: '6px' }}
                    onClick={() => setIsDescExpanded(!isDescExpanded)}
                  >
                    {isDescExpanded ? 'Read Less' : 'Read More'}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Highlights Section */}
          {location.highlights?.length > 0 && (
            <div className="bh-detail-section">
              <div className="bh-detail-section-title" style={{ color: `${primaryColor}99` }}>HIGHLIGHTS</div>
              <div className="bh-detail-highlights-row">
                {location.highlights.map((h, i) => (
                  <span
                    key={i}
                    className="bh-detail-highlight-tag"
                    style={{ color: primaryColor, borderColor: `${primaryColor}25`, background: `${primaryColor}0f` }}
                  >
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="6.5" cy="6.5" r="5.417" stroke={primaryColor} strokeWidth="1.083" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6.5 4.333v2.167l1.083 1.083" stroke={primaryColor} strokeWidth="1.083" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {h}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Visitor Tips Section */}
          {location.visitorTips?.length > 0 && (
            <div
              className="bh-detail-tips-card"
              style={{ background: '#fff8f0', borderColor: `${primaryColor}80` }}
            >
              <div className="bh-detail-section-title" style={{ color: primaryColor }}>VISITOR TIPS</div>
              <ul className="bh-detail-tips-list">
                {location.visitorTips.map((tip, i) => (
                  <li key={i} className="bh-detail-tip-item" style={{ color: primaryColor }}>
                    · {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right Sticky Card */}
        <div className="bh-detail-sticky-card">
          {/* Header: Begin Your Journey */}
          <div
            className="bh-detail-card-header"
            style={{ background: `${primaryColor}08`, borderBottomColor: `${primaryColor}12` }}
          >
            <p className="bh-detail-card-header-label" style={{ color: primaryColor }}>BEGIN YOUR JOURNEY</p>
          </div>

          <div className="bh-detail-card-body">
            {/* Book Now Button */}
            <button
              className="bh-detail-book-btn"
              style={{ background: primaryColor, boxShadow: `0 4px 14px ${primaryColor}40` }}
              onClick={() => history.push(`/booking-hub/${gid}/location/${locationGid}/next`)}
            >
              BOOK NOW
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.75 7.5h7.5M8.75 5l2.5 2.5-2.5 2.5" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Quick Info */}
            <div className="bh-detail-card-info" style={{ background: '#f5f0e8', borderRadius: 14 }}>
              {getTodayOperatingHours() && (
                <div className="bh-detail-card-info-row">
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="5.5" cy="5.5" r="4.583" stroke={primaryColor} strokeWidth="0.917" strokeLinecap="round"/>
                    <path d="M5.5 3.667V5.5l1.833 1.833" stroke={primaryColor} strokeWidth="0.917" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={{ color: '#9a8060' }}>{getTodayOperatingHours()}</span>
                </div>
              )}
              {location.address && (
                <div className="bh-detail-card-info-row" style={{ marginTop: 4 }}>
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.5 10.083S1.833 6.875 1.833 4.125a3.667 3.667 0 0 1 7.334 0c0 2.75-3.667 5.958-3.667 5.958z" stroke={primaryColor} strokeWidth="0.917" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="5.5" cy="4.125" r="0.917" stroke={primaryColor} strokeWidth="0.917"/>
                  </svg>
                  <span style={{ color: '#9a8060' }}>{formatAddress(location.address)}</span>
                </div>
              )}
            </div>

            {/* Footer note */}
            <p className="bh-detail-card-note" style={{ color: '#9a8060' }}>
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.167 1.833H1.833C1.281 1.833.917 2.198.917 2.75v5.5c0 .552.364.917.916.917h7.334c.552 0 .916-.365.916-.917v-5.5c0-.552-.364-.917-.916-.917z" stroke="#9a8060" strokeWidth="0.917" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9.167 3.208L5.5 5.958 1.833 3.208" stroke="#9a8060" strokeWidth="0.917" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Instant e-ticket.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingHubLocation;
